import React, { useEffect, useRef } from "react";
import * as THREE from "three";

// Utility function to create a vertical gradient texture from two colors.
function createGradientTexture(color1, color2) {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
  
    const gradient = context.createLinearGradient(0, 0, 0, size);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
  
    context.fillStyle = gradient;
    context.fillRect(0, 0, size, size);
  
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
}
  
const BubbleParticles = () => {
  const mountRef = useRef(null);
  
  useEffect(() => {
    const mount = mountRef.current;
    let width = window.innerWidth;
    let height = window.innerHeight;
  
    // Create scene, camera & renderer.
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 100;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);
  
    // Compute visible boundaries.
    const vFOV = THREE.MathUtils.degToRad(camera.fov);
    const visibleHeight = 2 * Math.tan(vFOV / 2) * camera.position.z;
    const visibleWidth = visibleHeight * camera.aspect;
    // Expand boundaries so bubbles can start off screen.
    const extra = 200;
    const xBound = (visibleWidth + extra) / 2;
    const yBound = (visibleHeight + extra) / 2;
    const zBound = 100; // Keep a narrow depth range
  
    // Define an expanded, bright neon color palette.
    const neonPalette = [
      "#39FF14", // Neon Green
      "#FF073A", // Neon Red
      "#FF6EC7", // Neon Pink
      "#0000FF", // Neon Blue
      "#00FFFF", // Neon Cyan
      "#BFFF00", // Neon Lime
      "#FFFF00", // Neon Yellow
      "#FF00FF", // Neon Magenta
      "#FF9100"  // Neon Orange
    ];
  
    // Create neon, glass-like bubbles.
    const bubbles = [];
    const numBubbles = 100;
    const bubbleRadius = 5;
    const bubbleGeometry = new THREE.SphereGeometry(bubbleRadius, 64, 64);
    
    for (let i = 0; i < numBubbles; i++) {
      const color1 = neonPalette[Math.floor(Math.random() * neonPalette.length)];
      const color2 = neonPalette[Math.floor(Math.random() * neonPalette.length)];
      const gradientMap = createGradientTexture(color1, color2);
      const material = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        map: gradientMap,
        emissive: color1,
        emissiveIntensity: 0.5,
        transparent: true,
        opacity: 0.6,
        metalness: 0.0,
        roughness: 0.0,
        clearcoat: 1.0,
        clearcoatRoughness: 0.0,
        transmission: 1.5,
        ior: 1.33,
        thickness: 0.5,
        side: THREE.DoubleSide
      });
  
      const bubble = new THREE.Mesh(bubbleGeometry, material);
      // Start off screen in a random location within the expanded boundaries.
      bubble.position.set(
        (Math.random() - 0.5) * (visibleWidth + extra),
        (Math.random() - 0.5) * (visibleHeight + extra),
        (Math.random() - 0.5) * 20
      );
  
      // Set random velocities for free trajectories.
      bubble.userData.velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,          // x velocity
        (Math.random() - 0.5) * 2,          // y velocity
        (Math.random() - 0.5) * 0.2         // z velocity
      );
  
      scene.add(bubble);
      bubbles.push(bubble);
    }
  
    // Add lighting to enhance reflections.
    const ambientLight = new THREE.AmbientLight(0xffffff, 2.0);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
    directionalLight.position.set(50, 50, 100);
    scene.add(directionalLight);
    const pointLight = new THREE.PointLight(0xffffff, 1.5, 500);
    pointLight.position.set(0, 0, 100);
    scene.add(pointLight);
  
    // Animation loop.
    const animate = () => {
      requestAnimationFrame(animate);
  
      // Move bubbles and bounce off the boundaries.
      bubbles.forEach(bubble => {
        bubble.position.add(bubble.userData.velocity);
  
        if (bubble.position.x > xBound || bubble.position.x < -xBound) {
          bubble.userData.velocity.x *= -1;
        }
        if (bubble.position.y > yBound || bubble.position.y < -yBound) {
          bubble.userData.velocity.y *= -1;
        }
        if (bubble.position.z > zBound || bubble.position.z < -zBound) {
          bubble.userData.velocity.z *= -1;
        }
  
        // Maintain a constant scale.
        bubble.scale.set(1, 1, 1);
      });
  
      // Collision detection and response.
      for (let i = 0; i < bubbles.length; i++) {
        for (let j = i + 1; j < bubbles.length; j++) {
          const a = bubbles[i];
          const b = bubbles[j];
          const distance = a.position.distanceTo(b.position);
          if (distance < bubbleRadius * 2) {
            // Calculate collision normal and penetration depth.
            const collisionNormal = new THREE.Vector3().subVectors(b.position, a.position).normalize();
            const penetrationDepth = bubbleRadius * 2 - distance;
            
            // Separate the bubbles by half the penetration distance each.
            a.position.add(collisionNormal.clone().multiplyScalar(-penetrationDepth / 2));
            b.position.add(collisionNormal.clone().multiplyScalar(penetrationDepth / 2));
  
            // Swap velocities.
            const temp = a.userData.velocity.clone();
            a.userData.velocity.copy(b.userData.velocity);
            b.userData.velocity.copy(temp);
          }
        }
      }
  
      renderer.render(scene, camera);
    };
  
    animate();
  
    // Handle window resize.
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
  
    window.addEventListener("resize", handleResize);
  
    return () => {
      window.removeEventListener("resize", handleResize);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);
  
  return (
    <div 
      ref={mountRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none"
      }}
    />
  );
};
  
export default BubbleParticles;
